import React from "react";
import { Box, Button } from "@mui/material";
import { SpecsInputField } from "../SpecsEditorFields/index";
import { logSpecsModelPath } from "../specsModelPathLogger";

function stringifyJson(value: unknown) {
  if (value === undefined) {
    return "";
  }

  return JSON.stringify(value, null, 2);
}

function isEmptyPlainObject(value: unknown) {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value as Record<string, unknown>).length === 0
  );
}

type SpecsJsonFieldProps = {
  label: string;
  value: unknown;
  minRows?: number;
  helperText?: string;
  debugPath?: string;
  clickToEdit?: boolean;
  onChange: (nextValue: unknown) => void;
};

export default function SpecsJsonField({
  label,
  value,
  minRows = 6,
  helperText,
  debugPath,
  clickToEdit,
  onChange,
}: SpecsJsonFieldProps) {
  const [inputValue, setInputValue] = React.useState(() =>
    stringifyJson(value)
  );
  const [error, setError] = React.useState<string | null>(null);
  const [isExpanded, setIsExpanded] = React.useState(
    () => !isEmptyPlainObject(value)
  );
  const [forceEdit, setForceEdit] = React.useState(false);

  React.useEffect(() => {
    const nextValue = stringifyJson(value);
    setInputValue(nextValue);
    setError(null);
    if (!isEmptyPlainObject(value)) {
      setIsExpanded(true);
    }
  }, [value]);

  if (!isExpanded && isEmptyPlainObject(value)) {
    return (
      <Box>
        <Box
          onClick={() => {
            if (debugPath) {
              logSpecsModelPath(debugPath);
            }
          }}
          sx={{
            mb: 0.5,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            width: "100%",
          }}
        >
          <Box component="span">{label}</Box>
          <Button
            variant="text"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setForceEdit(true);
              setIsExpanded(true);
            }}
            sx={{ minWidth: 0, p: 0, fontSize: 13, textTransform: "none" }}
          >
            Ajouter du JSON extra
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <SpecsInputField
      label={label}
      value={inputValue}
      multiline
      minRows={minRows}
      helperText={error ?? helperText}
      error={Boolean(error)}
      debugPath={debugPath}
      clickToEdit={forceEdit ? false : clickToEdit}
      onChange={(event) => {
        const nextValue = event.target.value;
        setInputValue(nextValue);

        if (!nextValue.trim()) {
          setError(null);
          onChange(Array.isArray(value) ? [] : {});
          return;
        }

        try {
          const parsed = JSON.parse(nextValue);
          setError(null);
          onChange(parsed);
        } catch (nextError) {
          setError(
            nextError instanceof Error ? nextError.message : "Invalid JSON"
          );
        }
      }}
    />
  );
}
